import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';
import { showToast } from '../../components/utils/Toast';

// Fetch safety content
export const fetchSafetyContent = createAsyncThunk(
  'safety/fetchSafetyContent',
  async (folder = 'root', { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/safety?folder=${folder}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch safety content');
      }

      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Upload safety content
export const uploadSafetyContent = createAsyncThunk(
  'safety/uploadSafetyContent',
  async (formData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/safety/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to upload safety content');
      }

      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Create folder
export const createFolder = createAsyncThunk(
  'safety/createFolder',
  async (folderData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/safety/folder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(folderData)
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to create folder');
      }

      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Delete safety content
export const deleteSafetyContent = createAsyncThunk(
  'safety/deleteSafetyContent',
  async (contentId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/safety/content/${contentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete safety content');
      }

      return contentId;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Delete folder
export const deleteFolder = createAsyncThunk(
  'safety/deleteFolder',
  async (folderId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/safety/folder/${folderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete folder');
      }

      return folderId;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

const safetySlice = createSlice({
  name: 'safety',
  initialState: {
    content: [],
    folders: [],
    loading: false,
    error: null,
    currentFolder: 'root',
    breadcrumbs: [{ name: 'Home', path: 'root' }]
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentFolder: (state, action) => {
      state.currentFolder = action.payload;
    },
    updateBreadcrumbs: (state, action) => {
      state.breadcrumbs = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch safety content
      .addCase(fetchSafetyContent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSafetyContent.fulfilled, (state, action) => {
        state.loading = false;
        state.content = action.payload.content;
        state.folders = action.payload.folders;
      })
      .addCase(fetchSafetyContent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      
      // Upload safety content
      .addCase(uploadSafetyContent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadSafetyContent.fulfilled, (state, action) => {
        state.loading = false;
        state.content = [action.payload, ...state.content];
        showToast('Safety content uploaded successfully', 'success');
      })
      .addCase(uploadSafetyContent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      
      // Create folder
      .addCase(createFolder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFolder.fulfilled, (state, action) => {
        state.loading = false;
        state.folders = [...state.folders, action.payload];
        showToast('Folder created successfully', 'success');
      })
      .addCase(createFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      
      // Delete safety content
      .addCase(deleteSafetyContent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSafetyContent.fulfilled, (state, action) => {
        state.loading = false;
        state.content = state.content.filter(item => item._id !== action.payload);
        showToast('Content deleted successfully', 'success');
      })
      .addCase(deleteSafetyContent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      
      // Delete folder
      .addCase(deleteFolder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFolder.fulfilled, (state, action) => {
        state.loading = false;
        state.folders = state.folders.filter(folder => folder._id !== action.payload);
        showToast('Folder deleted successfully', 'success');
      })
      .addCase(deleteFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      });
  }
});

export const { clearError, setCurrentFolder, updateBreadcrumbs } = safetySlice.actions;
export default safetySlice.reducer; 